import {
    CONFIRM_BOOKING,
    CONFIRM_BOOKING_SUCCESS,
    CONFIRM_BOOKING_FAILED,
    CLEAR_BOOKING
} from "../store/types";
import { RequestPushMsg } from '../other/NotificationFunctions';
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { onValue, push, set } from "firebase/database";
import { normalizeBookingPayload } from '../booking/utils/normalizeBookingPayload';
import { stripUndefinedDeep } from '../booking/utils/stripUndefinedDeep';
import { getEstimate } from './estimateactions'; // [NEW] Import for auto-retry fallback
import { getValidAuthToken } from './authactions';

export const clearBooking = () => (dispatch) => {
    dispatch({ type: CLEAR_BOOKING, payload: null });
};

// ============================================================================
// CONFIGURATION
// ============================================================================
const RAW_FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_URL || '';
const FUNCTIONS_BASE_URL = RAW_FUNCTIONS_BASE_URL.replace(/\/+$/, '');

// Feature flag: Force LEGACY mode (set to true if Functions are permanently down)
const FORCE_LEGACY_MODE = false;

// ============================================================================
// HELPERS
// ============================================================================
const safeParseJson = async (response) => {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
        const text = await response.text().catch(() => '');
        return { _nonJson: true, _text: text };
    }
    return response.json();
};

/**
 * Check if Functions endpoint is available
 * Returns true if SECURE mode can be used, false for LEGACY
 */
/** code mort: const checkFunctionsAvailable = async () => {
    if (FORCE_LEGACY_MODE) {
        //console.log('[BookingMode] FORCE_LEGACY_MODE enabled');
        return false;
    }

    if (!FUNCTIONS_BASE_URL) {
        //console.log('[BookingMode] No FUNCTIONS_BASE_URL configured -> LEGACY');
        return false;
    }

    // Quick health check with timeout
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(`${FUNCTIONS_BASE_URL}/getservertime`, {
            method: 'GET',
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        // If we get JSON back, functions are available
        const result = await safeParseJson(response);
        if (result._nonJson) {
            //console.log('[BookingMode] Functions returned non-JSON -> LEGACY');
            return false;
        }

        //console.log('[BookingMode] Functions available -> SECURE');
        return true;
    } catch (error) {
        //console.log('[BookingMode] Functions health check failed:', error.message, '-> LEGACY');
        return false;
    }
};*/

// ============================================================================
// MAIN BOOKING ACTION
// ============================================================================
export const addBooking = (bookingData) => async (dispatch) => {
    const { singleUserRef, bookingRef } = firebase;

    // [DEBUG] Deep payload inspection to identify pricingToken location
    //console.log('[ADD_BOOKING] Origin:', typeof window !== 'undefined' ? window.location.origin : 'SSR');
    //console.log('[ADD_BOOKING] Keys:', Object.keys(bookingData));
    //console.log('[ADD_BOOKING] Estimate Keys:', bookingData.estimate ? Object.keys(bookingData.estimate) : 'MISSING');
    //console.log('[ADD_BOOKING] EstimateData Keys:', bookingData.estimatedata ? Object.keys(bookingData.estimatedata) : 'MISSING');

    dispatch({ type: CONFIRM_BOOKING, payload: bookingData });

    // [SEC] Strictly prohibit booking for deleted or archived accounts
    const isTargetDeleted = Boolean(
        bookingData?.userDetails?.accountDeleted ||
        bookingData?.userDetails?.isDeleted ||
        bookingData?.onBehalfOfClient?.accountDeleted ||
        bookingData?.onBehalfOfClient?.isDeleted ||
        bookingData?.customer?.accountDeleted ||
        bookingData?.customer?.isDeleted
    );
    if (isTargetDeleted) {
        const error = new Error("Impossible de commander pour un compte supprimé ou archivé.");
        error.code = 'ACCOUNT_DELETED';
        dispatch({
            type: CONFIRM_BOOKING_FAILED,
            payload: { msg: error.message, code: 'ACCOUNT_DELETED' }
        });
        throw error;
    }

    // Determine booking mode
    // [FIX] Robust Lookup for pricingToken
    let pricingToken = null;
    let tokenSource = 'NONE';

    // [REMOVED] Frontend must NEVER generate idempotencyKey - it comes from backend\n    // See: payment_resume bug - frontend-generated tokens are rejected by backend

    if (bookingData?.estimate?.pricingToken) {
        pricingToken = bookingData.estimate.pricingToken;
        tokenSource = 'bookingData.estimate.pricingToken';
    } else if (bookingData?.estimatedata?.estimate?.pricingToken) {
        pricingToken = bookingData.estimatedata.estimate.pricingToken;
        tokenSource = 'bookingData.estimatedata.estimate.pricingToken';
    } else if (bookingData?.estimatedata?.pricingToken) {
        pricingToken = bookingData.estimatedata.pricingToken;
        tokenSource = 'bookingData.estimatedata.pricingToken';
    } else if (bookingData?.pricingToken) {
        pricingToken = bookingData.pricingToken;
        tokenSource = 'bookingData.pricingToken';
    }

    if (pricingToken) {
        //console.log(`[BOOKING_SECURE] pricingToken Found via ${tokenSource}. Length: ${pricingToken.length}`);
    } else {
        //console.warn('[BOOKING_SECURE] pricingToken MISSING in all paths. Dumping payload:', JSON.stringify(bookingData, null, 2));
    }

    // [BOOKING_SECURE] Functions Base URL Check
    //console.log('[ADD_BOOKING] FUNCTIONS_BASE_URL:', FUNCTIONS_BASE_URL);

    const canUseFunctions = !!FUNCTIONS_BASE_URL && !FORCE_LEGACY_MODE;

    // DECIDE: Strictly Enforce SECURE mode if available (SECURE-FIRST)
    if (canUseFunctions) {
        // [STRICT] If functions are online, we MUST use them.
        //console.log('[BOOKING_SECURE] calling createBooking (Mode: SECURE)');
        //console.log('[ADD_BOOKING] pricingToken=', pricingToken ? 'PRESENT' : 'MISSING');

        if (pricingToken) {
            return await executeSecureBooking(dispatch, bookingData, pricingToken, singleUserRef);
        } else {
            // CRITICAL: Prevent falling back to Legacy if token is just missing.
            // Throw error so UI can refresh token and retry.
            console.error('[Booking] Functions available but pricingToken missing. Blocking Legacy fallback.');
            const error = new Error("Prix expiré. Veuillez réactualiser.");
            error.code = 'TOKEN_MISSING'; // Handled by UI
            throw error;
        }
    } else {
        // [FALLBACK] Only if Functions are undeniably offline (rare/dev) or Force Legacy enabled
        // ====================================================================
        // LEGACY MODE: Direct RTDB write (fallback)
        // ====================================================================
        console.warn('[Booking] Functions unavailable/disabled -> Using LEGACY RTDB mode (ForceLegacy=' + FORCE_LEGACY_MODE + ')');
        return await executeLegacyBooking(dispatch, bookingData, bookingRef, singleUserRef);
    }
};

// --- DTO Sanitizers for createBooking ---
const cleanAddressLocation = (loc) => {
    if (!loc) return null;
    if (typeof loc === 'string') return { address: loc, add: loc, description: loc };
    const address = loc.address || loc.formatted_address || loc.description || loc.name || loc.add || "";
    let coords = null;
    if (loc.coords && typeof loc.coords === 'object') {
        const lat = typeof loc.coords.lat === 'function' ? loc.coords.lat() : Number(loc.coords.lat);
        const lng = typeof loc.coords.lng === 'function' ? loc.coords.lng() : Number(loc.coords.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) coords = { lat, lng };
    } else if (loc.lat !== undefined && loc.lng !== undefined) {
        const lat = typeof loc.lat === 'function' ? loc.lat() : Number(loc.lat);
        const lng = typeof loc.lng === 'function' ? loc.lng() : Number(loc.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) coords = { lat, lng };
    }
    const res = { address, add: address, description: address, coords, lat: coords?.lat || null, lng: coords?.lng || null };
    if (loc.waypoints && Array.isArray(loc.waypoints) && loc.waypoints.length > 0) {
        res.waypoints = loc.waypoints;
    }
    return res;
};

const cleanEstimateForBooking = (estimate) => {
    if (!estimate || typeof estimate !== 'object') return null;
    return {
        price: estimate.price ?? estimate.estimateFare ?? null,
        estimateFare: estimate.estimateFare ?? estimate.price ?? null,
        currency: estimate.currency || "EUR",
        durationSec: estimate.durationSec ?? null,
        durationMin: estimate.durationMin ?? null,
        tripMeta: estimate.tripMeta ? {
            distance: estimate.tripMeta.distance ?? null,
            distanceKm: estimate.tripMeta.distanceKm ?? null,
            durationMin: estimate.tripMeta.durationMin ?? null,
            durationSec: estimate.tripMeta.durationSec ?? null,
            carType: estimate.tripMeta.carType ?? null,
            scheduledAt: estimate.tripMeta.scheduledAt ?? null,
            tripType: estimate.tripMeta.tripType ?? null,
            returnEnabled: estimate.tripMeta.returnEnabled ?? false
        } : null
    };
};

const cleanRouteDetailsForBooking = (rd) => {
    if (!rd || typeof rd !== 'object') return null;
    return {
        distance: rd.distance ?? null,
        distanceKm: rd.distanceKm ?? (rd.distance ? rd.distance / 1000 : null),
        duration: rd.duration ?? rd.durationSec ?? null,
        durationSec: rd.durationSec ?? rd.duration ?? null,
        durationMin: rd.durationMin ?? null,
        provider: rd.provider || "google"
    };
};

const cleanReturnTripForBooking = (rt) => {
    if (!rt || typeof rt !== 'object' || !rt.enabled) return null;
    return {
        enabled: true,
        date: rt.date || rt.scheduledAt || null,
        scheduledAt: rt.scheduledAt || rt.date || null,
        tripdate: rt.tripdate || rt.scheduledAt || rt.date || null,
        time: rt.time || null,
        durationSec: rt.durationSec ?? null,
        durationMin: rt.durationMin ?? null,
        distance: rt.distance ?? null,
        distanceKm: rt.distanceKm ?? null,
        pricingToken: rt.pricingToken || null,
        price: rt.price ?? null,
        pickupFlightNumber: rt.pickupFlightNumber || rt.flightNumber || null,
        pickupTrainNumber: rt.pickupTrainNumber || rt.trainNumber || null,
        flightNumber: rt.flightNumber || rt.pickupFlightNumber || null,
        trainNumber: rt.trainNumber || rt.pickupTrainNumber || null,
        pickupAddress: rt.pickupAddress || rt.pickup?.address || null,
        dropAddress: rt.dropAddress || rt.drop?.address || null,
        pickup: rt.pickup || null,
        drop: rt.drop || null,
    };
};

// ============================================================================
// SECURE MODE: Functions-based booking
// ============================================================================
const executeSecureBooking = async (dispatch, bookingData, pricingToken, singleUserRef, retryCount = 0) => {
    const authState = store.getState().auth;
    const resolvedUserUid = bookingData.customer_uid || bookingData.customer?.uid || bookingData.userDetails?.uid || authState?.profile?.uid || authState?.info?.uid || null;
    const resolvedProfile = authState?.profile || bookingData.userDetails || bookingData.customer || {};

    const requestBody = {
        pricingToken,
        pickup: cleanAddressLocation(bookingData.pickup),
        drop: cleanAddressLocation(bookingData.drop),
        tripDate: bookingData.tripdate || bookingData.tripDate,
        carType: bookingData.carType || bookingData.carDetails?.name,
        is_booking_for_other: !!bookingData.is_booking_for_other,
        customer_uid: resolvedUserUid,
        passenger: {
            uid: resolvedUserUid || "guest",
            firstName: bookingData.userDetails?.firstName || bookingData.customer?.firstName || resolvedProfile.firstName || bookingData.customer_name?.split(' ')[0] || '',
            lastName: bookingData.userDetails?.lastName || bookingData.customer?.lastName || resolvedProfile.lastName || bookingData.customer_name?.split(' ').slice(1).join(' ') || '',
            email: bookingData.userDetails?.email || bookingData.customer?.email || resolvedProfile.email || bookingData.customer_email || '',
            phone: bookingData.userDetails?.mobile || bookingData.customer?.phone || resolvedProfile.mobile || resolvedProfile.phone || bookingData.customer_contact || ''
        },
        booker_details: bookingData.booker_details || resolvedProfile || null,
        paymentMode: bookingData.payment_mode,
        payment: bookingData.payment || null,
        walletDeducted: bookingData.walletDeducted || bookingData.payment?.walletDeducted || 0,
        bookLater: Boolean(bookingData.bookLater),
        booking_from_web: Boolean(bookingData.booking_from_web),
        options: {
            ...bookingData.instructionData,
            returnFlightNumber: bookingData.returnTrip?.pickupFlightNumber || bookingData.returnTrip?.flightNumber || bookingData.instructionData?.returnFlightNumber || null,
            returnTrainNumber: bookingData.returnTrip?.pickupTrainNumber || bookingData.returnTrip?.trainNumber || bookingData.instructionData?.returnTrainNumber || null,
            otp: bookingData.otp,
            driverInstructions: bookingData.driverInstructions || bookingData.instructionData?.driverNotes,
            optionDetails: bookingData.optionDetails
        },
        idempotencyKey: bookingData.idempotencyKey || null,
        recaptchaToken: bookingData.recaptchaToken || null,
        returnTrip: cleanReturnTripForBooking(bookingData.returnTrip),
        language: bookingData.language || 'en',
        locale: bookingData.locale || (bookingData.language === 'en' ? 'en' : 'fr'), // [FIX] Send locale to backend
        dateLocale: bookingData.dateLocale || (bookingData.language === 'en' ? 'en-gb' : 'fr'), // [FIX] Send dateLocale to backend
        reference: bookingData.reference || null,

        // [FIX] B2B Billing — CRITICAL: without this, invoiceService sees billing=undefined
        // and skips invoice generation entirely for online bookings
        billing: bookingData.billing || null,

        // [SEC] Clean canonical DTO: forward ONLY essential numeric fields, NO vehicles[] or full Google maps
        estimate: cleanEstimateForBooking(bookingData.estimate),
        tripdate: bookingData.tripdate || null,
        scheduledAt: bookingData.scheduledAt || bookingData.tripdate || null,
        durationSec: bookingData.durationSec || null,
        durationMin: bookingData.durationMin || null,
        duration_source: bookingData.duration_source || null,
        distance: bookingData.distance || null,
        distanceKm: bookingData.distanceKm || null,
        routeDetails: cleanRouteDetailsForBooking(bookingData.routeDetails),
        trip_details: bookingData.trip_details ? {
            pickup_address: bookingData.trip_details.pickup_address || "",
            drop_address: bookingData.trip_details.drop_address || "",
            price: bookingData.trip_details.price,
            currency: bookingData.trip_details.currency || "EUR",
        } : null,
        instructionData: bookingData.instructionData || null,
        booked_by_admin: bookingData.booked_by_admin || false,
        admin_details: bookingData.admin_details || null,

        // Promo code & discount support
        promo_code: bookingData.promo_code || null,
        discount: bookingData.discount || bookingData.promo_discount || 0,
        promo_discount: bookingData.promo_discount || bookingData.discount || 0,
        promo_details: bookingData.promo_details || null,
    };

    const resolvedWaypoints = (Array.isArray(bookingData.waypoints) && bookingData.waypoints.length > 0)
        ? bookingData.waypoints
        : (Array.isArray(bookingData.drop?.waypoints) && bookingData.drop.waypoints.length > 0)
            ? bookingData.drop.waypoints
            : null;

    if (resolvedWaypoints) {
        requestBody.waypoints = resolvedWaypoints;
    }

    try {
        const url = `${FUNCTIONS_BASE_URL}/createBooking`;

        // [SEC] Send auth token so backend can identify logged-in users (Zero-Trust)
        const headers = { 'Content-Type': 'application/json' };
        try {
            let authToken = null;
            let currentUser = null;
            if (typeof firebase.authRef === 'function') {
                const a = firebase.authRef();
                currentUser = a?.currentUser;
            }
            if (!currentUser && firebase.auth) {
                currentUser = typeof firebase.auth === 'function' ? firebase.auth().currentUser : firebase.auth.currentUser;
            }
            if (currentUser && typeof currentUser.getIdToken === 'function') {
                authToken = await currentUser.getIdToken();
            }
            if (!authToken && typeof getValidAuthToken === 'function') {
                authToken = await getValidAuthToken();
            }
            if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
            }
        } catch (_tokenErr) {
            console.warn('[executeSecureBooking] Non-blocking: Could not get ID token:', _tokenErr);
        }

        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(requestBody)
        });

        const result = await safeParseJson(response);

        if (result && result._nonJson) {
            throw new Error("SERVER_ERROR: Non-JSON response from /createBooking");
        }

        if (!response.ok) {
            let errorMessage = result?.message || 'Erreur lors de la réservation.';
            const errCode = result?.error;

            // [FIX] Auto-Retry Logic for Token Errors
            if ((errCode === 'TOKEN_ALREADY_USED' || errCode === 'TOKEN_EXPIRED') && retryCount < 1) {
                console.warn(`[createBooking] Token Error (${errCode}). Retrying with fresh estimate...`);

                // Notify User (Optional: dispatch ephemeral loading message if supported)
                dispatch({
                    type: CONFIRM_BOOKING_FAILED,
                    payload: { msg: null, code: 'RETRYING_ESTIMATE' }
                });

                // 1. Re-fetch Estimate
                const estimateResult = await dispatch(getEstimate(bookingData));
                if (estimateResult && estimateResult.payload && estimateResult.payload.pricingToken) {
                    const newToken = estimateResult.payload.pricingToken;
                    //console.log('[createBooking] Got fresh token. Retrying booking...');
                    // 2. Retry Booking with new token
                    // Update estimate in bookingData for consistency (optional but good)
                    bookingData.estimate = { ...bookingData.estimate, pricingToken: newToken };
                    return await executeSecureBooking(dispatch, bookingData, newToken, singleUserRef, retryCount + 1);
                } else {
                    console.error('[createBooking] Failed to refresh estimate for retry.');
                    throw new Error("ESTIMATE_REFRESH_FAILED");
                }
            }

            // [FIX] Pass backend message + error code to reducer — frontend handles i18n translation
            let safeMsg = typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage);

            dispatch({
                type: CONFIRM_BOOKING_FAILED,
                payload: { msg: safeMsg, code: errCode || 'BOOKING_ERROR' }
            });
            const err = new Error(safeMsg);
            err.code = errCode;
            throw err;
        }

        const bookingKey = result.bookingId;

        // Push notifications safely
        try {
            notifyRequestedDrivers(bookingData.requestedDrivers, singleUserRef);
        } catch (pushErr) {
            console.warn('[executeSecureBooking] Driver push notification error (non-fatal):', pushErr);
        }

        dispatch({
            type: CONFIRM_BOOKING_SUCCESS,
            payload: {
                booking_id: bookingKey,
                mainData: { ...bookingData, id: bookingKey, confirmedPrice: result.price }
            }
        });

        return result;

    } catch (error) {
        // [FIX] Allow retry bubble up
        if (error.message && error.message.includes("refresh")) throw error;

        const isNetwork = String(error?.message || '').toLowerCase().includes('fetch');
        if (isNetwork) {
            dispatch({
                type: CONFIRM_BOOKING_FAILED,
                payload: { msg: null, code: 'NETWORK_ERROR' }
            });
        }
        throw error;
    }
};

// ============================================================================
// LEGACY MODE: Direct RTDB booking (fallback)
// ============================================================================
const executeLegacyBooking = async (dispatch, bookingData, bookingRef, singleUserRef) => {
    console.warn('[Booking] Using LEGACY mode - Functions unavailable or pricingToken missing');

    try {
        // [CANONICAL] Use shared Canonical v1 Normalizer
        const bookingObject = normalizeBookingPayload(bookingData);
        bookingObject.bookingMode = "LEGACY"; // Enforce mode flag because normalize sets default, but we want to be explicit here

        /**console.log('[BOOKING_PAYLOAD] Normalized:', {
            id: bookingObject.id,
            pickupAddress: bookingObject.pickupAddress,
            dropAddress: bookingObject.dropAddress,
            returnTrip: bookingObject.returnTrip
        });*/

        // Add requested drivers (preserving legacy logic for push notifications)
        if (bookingData.requestedDrivers) {
            if (bookingData.bookLater) {
                bookingObject.preRequestedDrivers = bookingData.requestedDrivers;
            } else {
                bookingObject.requestedDrivers = bookingData.requestedDrivers;
            }
        }

        // [GUARD] Critical Data Check
        if (!bookingObject.pickupAddress || bookingObject.pickupAddress === 'Unknown Address') {
            // Fallback: If still unknown, accept it but WARN, or if strictly required, throw logic error?
            // Since user might have selected map point without address, "Unknown Address" + Coords is valid.
            console.warn('Legacy Booking: Address is Unknown, but continuing with coords.');
        }

        /**console.log('[BOOKING_PAYLOAD]', {
            pickupAddress: bookingObject.pickupAddress,
            dropAddress: bookingObject.dropAddress,
            returnTrip: bookingObject.returnTrip,
            pickup: bookingObject.pickup,
            drop: bookingObject.drop
        });*/

        // Add requested drivers
        if (bookingData.requestedDrivers) {
            if (bookingData.bookLater) {
                bookingObject.preRequestedDrivers = bookingData.requestedDrivers;
            } else {
                bookingObject.requestedDrivers = bookingData.requestedDrivers;
            }
        }

        // [FIX] STRIP UNDEFINED (Deep)
        const cleanPayload = stripUndefinedDeep(bookingObject);
        //console.log('[LEGACY] cleanPayload ready for RTDB:', cleanPayload);

        // Write to RTDB
        const newBookingRef = push(bookingRef);
        await set(newBookingRef, cleanPayload);

        const bookingKey = newBookingRef.key;
        //console.log('[Booking] LEGACY booking created:', bookingKey);

        // Push notifications
        notifyRequestedDrivers(bookingData.requestedDrivers, singleUserRef);

        dispatch({
            type: CONFIRM_BOOKING_SUCCESS,
            payload: {
                booking_id: bookingKey,
                mainData: { ...bookingData, id: bookingKey }
            }
        });

        return { bookingId: bookingKey };

    } catch (error) {
        console.error('[Booking] LEGACY mode failed:', error);
        const msg = error.message || 'Erreur lors de la réservation (Legacy).';
        dispatch({
            type: CONFIRM_BOOKING_FAILED,
            payload: { msg: msg, code: 'LEGACY_WRITE_FAILED' }
        });
        throw error;
    }
};

// ============================================================================
// SHARED: Push notifications to requested drivers
// ============================================================================
const notifyRequestedDrivers = (requestedDrivers, singleUserRef) => {
    if (!requestedDrivers) return;

    Object.keys(requestedDrivers).forEach((uid) => {
        try {
            onValue(singleUserRef(uid), snapshot => {
                const v = snapshot.val();
                const pushToken = v?.pushToken;
                if (pushToken) {
                    const lang = store.getState().languagedata?.defaultLanguage;
                    RequestPushMsg(pushToken, {
                        title: lang?.notification_title || 'Notification',
                        msg: lang?.new_booking_notification || 'Nouvelle réservation reçue',
                        screen: 'DriverTrips'
                    });
                }
            }, { onlyOnce: true });
        } catch (err) {
            console.warn('[notifyRequestedDrivers] Error for driver', uid, err);
        }
    });
};
